import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { StickyNote, HelpCircle, Plus, Trash2, Check, RotateCcw } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Card, Button, Spinner } from "@/components/ui";
import { fmtDate } from "@/lib/dates";

// =============================================================================
// BidNotepadCard — scratchpad + RFI tracker for a BID.
//   • Notes  — one shared free-text pad (autosaves), for working notes while
//              quoting (assumptions, pricing thoughts, things to confirm).
//   • RFIs   — questions raised with the client, tracked open → answered, so
//              you can see at a glance what's still outstanding to go back on.
// Manager+ only (migration 192 RLS). Mirrors the project notepad pattern.
// =============================================================================

interface Props { bidId: string; bidNumber: string; canEdit: boolean; }

type Tab = "notes" | "rfis";

export default function BidNotepadCard({ bidId, bidNumber, canEdit }: Props) {
  const [tab, setTab] = useState<Tab>("notes");

  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <StickyNote size={14} className="text-text-muted" />
            <span className="text-[12px] uppercase tracking-wider text-text-muted">Quote notes &amp; RFIs</span>
          </div>
          <span className="text-[10px] text-text-dim italic hidden sm:inline">scratchpad — helps you quote &amp; track questions</span>
        </div>

        <div className="flex items-center gap-1 mb-3 border-b border-line/60">
          <TabBtn active={tab === "notes"} onClick={() => setTab("notes")} icon={StickyNote}>Notes</TabBtn>
          <TabBtn active={tab === "rfis"}  onClick={() => setTab("rfis")}  icon={HelpCircle}>RFIs</TabBtn>
        </div>

        {tab === "notes" && <NotesPanel bidId={bidId} canEdit={canEdit} />}
        {tab === "rfis"  && <RfisPanel  bidId={bidId} bidNumber={bidNumber} canEdit={canEdit} />}
      </div>
    </Card>
  );
}

function TabBtn({ active, onClick, icon: Icon, children }: { active: boolean; onClick: () => void; icon: any; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-[12px] font-medium border-b-2 -mb-px inline-flex items-center gap-1.5 transition-colors ${
        active ? "border-accent text-text" : "border-transparent text-text-muted hover:text-text"
      }`}
    >
      <Icon size={12} /> {children}
    </button>
  );
}

// ------------------------------------------------------------------- Notes ---
function NotesPanel({ bidId, canEdit }: { bidId: string; canEdit: boolean }) {
  const user = useRequireAuth();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["bid_note", bidId],
    queryFn: async () => {
      const { data } = await supabase.from("bid_notes")
        .select("body, updated_at, users:updated_by ( employees:employee_id (first_name, last_name) )")
        .eq("bid_id", bidId).maybeSingle();
      return data ?? { body: "", updated_at: null, users: null };
    },
  });

  const [body, setBody] = useState("");
  const [hydrated, setHydrated] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!hydrated && data) {
      setBody((data as any).body ?? "");
      if ((data as any).updated_at) setSavedAt(new Date((data as any).updated_at));
      setHydrated(true);
    }
  }, [data, hydrated]);

  const mut = useMutation({
    mutationFn: async (next: string) => {
      const { error } = await supabase.from("bid_notes")
        .upsert({ bid_id: bidId, body: next, updated_by: user?.user_id ?? null, updated_at: new Date().toISOString() }, { onConflict: "bid_id" });
      if (error) throw error;
    },
    onSuccess: () => { setSavedAt(new Date()); qc.invalidateQueries({ queryKey: ["bid_note", bidId] }); },
  });

  function schedule(next: string) {
    setBody(next);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => mut.mutate(next), 800);
  }
  function flush() {
    if (debounceRef.current) { window.clearTimeout(debounceRef.current); debounceRef.current = null; mut.mutate(body); }
  }

  if (isLoading) return <div className="flex justify-center py-4"><Spinner size={14} /></div>;

  const editedBy = (data as any)?.users?.employees
    ? `${(data as any).users.employees.first_name} ${(data as any).users.employees.last_name}` : null;

  return (
    <div className="space-y-2">
      <textarea
        value={body}
        onChange={(e) => schedule(e.target.value)}
        onBlur={flush}
        disabled={!canEdit}
        placeholder="Working notes for this quote — assumptions, pricing thoughts, plant/subbie to price, things to confirm with the client. Shared with the internal team."
        rows={7}
        className="w-full bg-ink border border-line rounded-md px-3 py-2 text-[13px] text-text resize-y min-h-[120px] focus:outline-none focus:border-accent placeholder:text-text-dim/70 disabled:opacity-70"
      />
      <div className="flex items-center justify-between text-[10px] text-text-dim">
        <span>{mut.isPending ? "Saving…" : savedAt ? `Saved · ${savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}</span>
        {editedBy && <span>Last edit: {editedBy}</span>}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------- RFIs ---
interface Rfi {
  rfi_id: string; question: string; answer: string | null; status: "open" | "answered";
  created_at: string; answered_at: string | null;
}

function RfisPanel({ bidId, bidNumber, canEdit }: { bidId: string; bidNumber: string; canEdit: boolean }) {
  const user = useRequireAuth();
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");

  const { data: rfis = [], isLoading } = useQuery({
    queryKey: ["bid_rfis", bidId],
    queryFn: async () => {
      const { data } = await supabase.from("bid_rfis")
        .select("rfi_id, question, answer, status, created_at, answered_at")
        .eq("bid_id", bidId)
        .order("status", { ascending: true })   // open first
        .order("created_at", { ascending: false });
      return (data ?? []) as Rfi[];
    },
  });

  const addMut = useMutation({
    mutationFn: async () => {
      const q = draft.trim(); if (!q) return;
      const { error } = await supabase.from("bid_rfis").insert({ bid_id: bidId, question: q, created_by: user?.user_id ?? null });
      if (error) throw error;
    },
    onSuccess: () => { setDraft(""); qc.invalidateQueries({ queryKey: ["bid_rfis", bidId] }); },
  });

  const patchMut = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<Rfi> & { answered_at?: string | null } }) => {
      const { error } = await supabase.from("bid_rfis").update(patch).eq("rfi_id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bid_rfis", bidId] }),
  });

  const delMut = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from("bid_rfis").delete().eq("rfi_id", id); if (error) throw error; },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bid_rfis", bidId] }),
  });

  const open = rfis.filter(r => r.status === "open");
  const answered = rfis.filter(r => r.status === "answered");

  return (
    <div className="space-y-3">
      {canEdit && (
        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 bg-ink border border-line rounded-md px-3 py-2 focus-within:border-accent">
            <Plus size={13} className="text-text-muted flex-shrink-0" />
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && draft.trim()) { e.preventDefault(); addMut.mutate(); } }}
              placeholder="Log an RFI / question to go back to the client…"
              className="flex-1 bg-transparent text-[13px] text-text placeholder:text-text-dim/70 focus:outline-none"
            />
          </div>
          <Button size="sm" icon={Plus} onClick={() => draft.trim() && addMut.mutate()} disabled={!draft.trim() || addMut.isPending}>Add</Button>
        </div>
      )}

      {isLoading && <div className="flex justify-center py-3"><Spinner size={14} /></div>}

      {!isLoading && rfis.length === 0 && (
        <div className="text-[12px] text-text-muted py-2 italic">No RFIs yet. Log any question you need the client to answer before you can price it.</div>
      )}

      {open.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-wider text-amber-500">Open · {open.length}</div>
          {open.map(r => <RfiRow key={r.rfi_id} rfi={r} canEdit={canEdit}
            onAnswer={(answer) => patchMut.mutate({ id: r.rfi_id, patch: { answer, status: "answered", answered_at: new Date().toISOString() } })}
            onReopen={() => patchMut.mutate({ id: r.rfi_id, patch: { status: "open", answered_at: null } })}
            onSaveAnswer={(answer) => patchMut.mutate({ id: r.rfi_id, patch: { answer } })}
            onDelete={() => delMut.mutate(r.rfi_id)} />)}
        </div>
      )}

      {answered.length > 0 && (
        <details className="text-[11px] text-text-muted">
          <summary className="cursor-pointer hover:text-text select-none">{answered.length} answered</summary>
          <div className="mt-1.5 space-y-1.5">
            {answered.map(r => <RfiRow key={r.rfi_id} rfi={r} canEdit={canEdit}
              onAnswer={(answer) => patchMut.mutate({ id: r.rfi_id, patch: { answer, status: "answered", answered_at: new Date().toISOString() } })}
              onReopen={() => patchMut.mutate({ id: r.rfi_id, patch: { status: "open", answered_at: null } })}
              onSaveAnswer={(answer) => patchMut.mutate({ id: r.rfi_id, patch: { answer } })}
              onDelete={() => delMut.mutate(r.rfi_id)} />)}
          </div>
        </details>
      )}

      <p className="text-[10px] text-text-dim italic pt-1">{bidNumber} · track what you're waiting on the client for. Mark answered once they come back.</p>
    </div>
  );
}

function RfiRow({ rfi, canEdit, onAnswer, onReopen, onSaveAnswer, onDelete }: {
  rfi: Rfi; canEdit: boolean;
  onAnswer: (answer: string) => void;
  onReopen: () => void;
  onSaveAnswer: (answer: string) => void;
  onDelete: () => void;
}) {
  const [answer, setAnswer] = useState(rfi.answer ?? "");
  const answered = rfi.status === "answered";

  return (
    <div className={`group rounded-md border px-3 py-2 ${answered ? "border-line bg-ink-raised/40 opacity-80" : "border-amber-500/30 bg-amber-500/5"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[13px] text-text flex-1">{rfi.question}</div>
        {canEdit && (
          <button onClick={onDelete} className="opacity-0 group-hover:opacity-100 transition p-1 rounded text-text-muted hover:text-lost hover:bg-lost/10 flex-shrink-0" title="Delete">
            <Trash2 size={11} />
          </button>
        )}
      </div>
      <div className="text-[10px] text-text-dim mt-0.5">
        raised {fmtDate(rfi.created_at)}{rfi.answered_at ? ` · answered ${fmtDate(rfi.answered_at)}` : ""}
      </div>

      {canEdit && (
        <div className="mt-2 flex items-start gap-2">
          <input
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            onBlur={() => { if (answer !== (rfi.answer ?? "")) onSaveAnswer(answer.trim() || ""); }}
            placeholder="Client's answer (optional)…"
            className="flex-1 bg-ink border border-line rounded px-2.5 py-1.5 text-[12px] text-text focus:outline-none focus:border-accent placeholder:text-text-dim/70"
          />
          {answered ? (
            <Button size="sm" variant="secondary" icon={RotateCcw} onClick={onReopen}>Reopen</Button>
          ) : (
            <Button size="sm" icon={Check} onClick={() => onAnswer(answer.trim() || "")}>Answered</Button>
          )}
        </div>
      )}
      {!canEdit && rfi.answer && <div className="mt-1 text-[12px] text-text-muted">↳ {rfi.answer}</div>}
    </div>
  );
}
